"use client";
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { brandConfig } from "../../config/brand";
import CyberButton from "../ui/CyberButton";

export type SimpleRouletteItem = {
  id: number;
  name: string;
  image: string;
  reward?: string;
  accent?: string;
};

interface Props {
  items: SimpleRouletteItem[];
  winner: SimpleRouletteItem;
  spinMs?: number;
  onClose?: () => void;
}

const WINNER_PHRASES = [
  "CONGRATULATIONS!",
  "PROTOCOL COMPLETE!",
  "MISSION ACCOMPLISHED!",
  "SEQUENCE UNLOCKED!",
  "YOU DID IT!",
  "REWARD DEPLOYED!",
  "DATASTREAM VICTORY!",
  "LOOT SYNCHRONIZED!",
  "SUCCESS: OPERATION HARKAN!",
  "NEURAL LINK STABILIZED!",
];

const TOTAL_CARDS = 70;
const WIN_SLOT = 60;

export default function SimpleRoulette({
  items,
  winner,
  spinMs = 8000,
  onClose,
}: Props) {
  const [showWinner, setShowWinner] = useState(false);
  const [highlightWinner, setHighlightWinner] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isSpinningRef = useRef(false);

  // Generate row only once on mount
  const [row] = useState<SimpleRouletteItem[]>(() => {
    const out: SimpleRouletteItem[] = [];
    for (let i = 0; i < TOTAL_CARDS; i++) {
      if (i === WIN_SLOT) out.push(winner);
      else out.push(items[Math.floor(Math.random() * items.length)]);
    }
    return out;
  });

  const [winnerTitle] = useState(
    WINNER_PHRASES[Math.floor(Math.random() * WINNER_PHRASES.length)]
  );

  // Trigger spin after short layout delay - only once
  useEffect(() => {
    if (!containerRef.current || row.length === 0) return;

    // 🚫 Skip if already spinning
    if (isSpinningRef.current) return;

    let cancelled = false;
    let spinStarted = false;

    const triggerSpin = () => {
      if (spinStarted || cancelled) return;
      spinStarted = true;
      isSpinningRef.current = true;

      console.log("[Roulette] Starting spin...", {
        winner,
        spinMs,
        totalCards: row.length,
        itemsCount: items.length,
      });

      const spinTimeout = setTimeout(() => {
        if (!cancelled) startSpin();
      }, 150);

      // Cleanup for safety
      return () => clearTimeout(spinTimeout);
    };

    // 🧠 React Strict Mode runs effects twice (mount → unmount → mount)
    // So we defer execution until the "real" mount frame settles
    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(triggerSpin);
      // cancel both if unmounted early
      const cleanup = () => {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
        cancelled = true;
        isSpinningRef.current = false;
      };
      return cleanup;
    });

    // ensure cleanup on unmount
    return () => {
      cancelAnimationFrame(raf1);
      cancelled = true;
      isSpinningRef.current = false;
    };
  }, [row.length]);

  function startSpin() {
    const el = containerRef.current!;
    const vw = window.innerWidth;

    let cardW = 320;
    if (vw <= 480) cardW = 160;
    else if (vw <= 768) cardW = 220;

    const totalWidth = TOTAL_CARDS * cardW;
    const randomOffset = (Math.random() - 0.5) * cardW * 0.4;
    const targetX = -(WIN_SLOT * cardW - vw / 2 + cardW / 2 + randomOffset);
    const startX = vw / 2 - totalWidth * 0.3;

    // Reset once, then animate forward only once
    el.style.transition = "none";
    el.style.transform = `translateX(${startX}px)`;

    // force reflow
    el.getBoundingClientRect();

    // apply final spin transition
    requestAnimationFrame(() => {
      el.style.transition = `transform ${spinMs}ms cubic-bezier(0.1, 0.7, 0.3, 1.02)`;
      el.style.transform = `translateX(${targetX}px)`;
    });

    // effects
    const glowTimer = setTimeout(() => setHighlightWinner(true), spinMs - 400);
    const revealTimer = setTimeout(() => setShowWinner(true), spinMs + 700);

    // cleanup
    return () => {
      clearTimeout(glowTimer);
      clearTimeout(revealTimer);
    };
  }

  return (
    <div className="lootbox-open-roulette-wrapper">
      <motion.div
        className="lootbox-open-roulette"
        initial={{ opacity: 0, y: 50, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -50, scale: 1.03 }}
        transition={{ duration: 0.8, ease: "easeInOut" }}
      >
        {/*  Focus Indicator */}
        <div className="lootbox-focus-overlay">
          <div className="lootbox-ticker-line" />
          <img
            src="/assets/icons/lootbox-ticker.svg"
            alt=""
            className="ticker-arrow top"
          />
          <img
            src="/assets/icons/lootbox-ticker.svg"
            alt=""
            className="ticker-arrow bottom"
          />
        </div>

        {/* Spinning row */}
        <div className="lootbox-open-row-container" ref={containerRef}>
          <div className="lootbox-open-row">
            {row.map((it, idx) => (
              <div
                key={idx}
                className={`lootbox-open-card ${
                  idx === WIN_SLOT && highlightWinner ? "highlight" : ""
                }`}
                style={{
                  transition: "box-shadow 0.6s ease-out",
                  boxShadow:
                    idx === WIN_SLOT && it.accent && highlightWinner
                      ? `0 0 30px 8px ${it.accent}`
                      : "none",
                }}
              >
                <img src={it.image} alt={it.name} />
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Winner popup */}
      {showWinner && winner && (
        <motion.div
          className="fixed inset-0 flex items-center justify-center bg-black/90 z-[1000] backdrop-blur-lg"
          initial={{ opacity: 0, scale: 0.9, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 1.1, ease: "easeOut" }}
        >
          <div className="flex flex-col items-center justify-center text-center w-full h-full px-6 space-y-8">
            <h2 className="text-4xl md:text-6xl font-bold text-white tracking-wide">
              <span
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: brandConfig.colors.gradient }}
              >
                {winnerTitle}
              </span>
            </h2>

            <motion.div
              className="relative w-[80vw] max-w-[420px] rounded-lg overflow-hidden"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            >
              <img
                src={winner.image}
                alt={winner.name}
                className="w-full h-auto max-h-[65vh] object-cover"
              />
              <div className="absolute bottom-4 left-4 text-left">
                <p
                  className="text-xl font-bold text-yellow-400 drop-shadow-md"
                  style={{
                    textShadow:
                      "0 0 4px rgba(0,0,0,0.8), 0 0 8px rgba(0,0,0,0.6)",
                  }}
                >
                  +{winner.reward}
                </p>
                <p className="text-xl text-gray-300 uppercase tracking-wider">
                  {winner.name}
                </p>
              </div>
            </motion.div>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 mt-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <CyberButton
                variant="outline"
                onClick={() => alert("share functionality coming soon")}
              >
                SHARE YOUR WIN
              </CyberButton>
              <CyberButton onClick={onClose}>CLAIM REWARD</CyberButton>
            </motion.div>
          </div>
        </motion.div>
      )}
    </div>
  );
}
